import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { CompanyUserQuery } from 'interfaceAdapter/queries/CompanyUser';
import { CompanyRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/CompanyRegistrationProfileInfo';
import { CompanyEditRepository } from 'interfaceAdapter/repositories/CompanyEdit';
import { PROFILE_STAGE } from 'domain/entities/CompanyUser/CompanyUser';
import { useNavigate } from 'react-router-dom';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        area: yup.string().required(),
        number_of_employees: yup.string().required(),
        revenue_scale: yup.string().required(),
        profit_level: yup.string().required(),
        net_assets_level: yup.string().required(),
        invitation_code: yup.string().max(100),
    })
    .required();

type CompanyForm = yup.InferType<typeof schema>;

export const useCompanyProfileRegistration = () => {
    const diContainer = useContext(diContainerContext);
    const companyRegistrationProfileInfoQuery = diContainer.resolve(CompanyRegistrationProfileInfoQuery);
    const companyUserQuery = diContainer.resolve(CompanyUserQuery);
    const companyEditRepository = diContainer.resolve(CompanyEditRepository);
    const { loading, setLoading } = useLoading();
    const [areaChoices, setAreaChoices] = useState<string[][]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [profitLevelChoices, setProfitLevelChoices] = useState<string[][]>([]);
    const [netAssetsLevelChoices, setNetAssetsLevelChoices] = useState<string[][]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
    } = useForm<CompanyForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    useEffect(() => {
        const checkCanEnter = async () => {
            const { user, status } = await companyUserQuery.checkToken(true);

            if (status === false) {
                // 有効なトークンが無いのでリダイレクト
                navigate('/company/login/');
            } else if (user.account_profile_stage !== PROFILE_STAGE.ONLY_PASSWORD) {
                navigate('/company/dashboard/');
            }
        };
        checkCanEnter();
    }, [window.location]);

    useEffect(() => {
        const fetchData = async () => {
            const data = await companyRegistrationProfileInfoQuery.fetch();
            setAreaChoices(data.area_choices);
            setEmployeeChoices(data.employee_choices);
            setRevenueChoices(data.revenue_choices);
            setProfitLevelChoices(data.profit_level_choices);
            setNetAssetsLevelChoices(data.net_assets_level_choices);
        };
        fetchData();
    }, []);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            await companyEditRepository.edit({
                ...data,
            });
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/company/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    return {
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        employeeChoices,
        revenueChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
    };
};
